import { ROUTES } from '#/config/routes';
import { formatDateOrDash } from '#/utils/date';

import {
  rememberAsyncFilterLabels,
  resolveFromLabelCache,
} from '#/modules/commonComponents/table/asyncFilterLabelCache';
import {
  createAsyncMultiFilterMeta,
  createDateFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { createDocumentNavLinksColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import { searchCustomerFilterOptions } from '#/modules/crmMgmt/customer/helper/searchCustomerFilterOptions';
import SignReceiptStatusBadge from '#/modules/crmMgmt/aftersales/signReceipt/components/SignReceiptStatusBadge';
import {
  readSignReceiptDateRangeFilter,
  resolveSignReceiptDateFilterChipDisplay,
} from '#/modules/crmMgmt/aftersales/signReceipt/helper/signReceiptDateListFilter';
import {
  SIGN_RECEIPT_STATUS,
  SIGN_RECEIPT_STATUS_LABELS,
} from '#/modules/crmMgmt/aftersales/signReceipt/helper/signReceiptStatus';

const SIGN_RECEIPT_STATUS_FILTER_OPTIONS = Object.entries(SIGN_RECEIPT_STATUS_LABELS).map(
  ([value, label]) => ({ value, label }),
);

function displayDash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

// BE sort 白名單：sign_receipt_no、sign_receipt_date、status、customer_name、created_at
export function buildSignReceiptColumns({
  filters = {},
  onFilterChange,
  onSignReceiptDateRangeChange,
  customerLabelCache,
} = {}) {
  return [
    createDocumentNavLinksColumn({
      getDetailPath: (row) => ROUTES.signReceiptDetail(row.id),
      getEditPath: (row) => ROUTES.signReceiptEdit(row.id),
      canShowEdit: (row) => row.status === SIGN_RECEIPT_STATUS.DRAFT,
      size: 100,
      minSize: 100,
    }),
    {
      id: 'signReceiptNo',
      header: '簽收單號',
      accessorKey: 'signReceiptNo',
      accessorFn: (row) => displayDash(row.signReceiptNo),
      size: 140,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'signReceiptNo',
        label: '簽收單號',
        filterValue: filters.signReceiptNo,
        onChange: (value) => onFilterChange('signReceiptNo', value),
        placeholder: '搜尋簽收單號',
      }),
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 110,
      cell: ({ row }) => (
        row.original.status
          ? <SignReceiptStatusBadge status={row.original.status} size="sm" />
          : '—'
      ),
      enableColumnFilter: true,
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: SIGN_RECEIPT_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange('status', value),
        }),
      },
    },
    {
      id: 'signReceiptDate',
      header: '預計送交日',
      accessorKey: 'signReceiptDate',
      accessorFn: (row) => formatDateOrDash(row.signReceiptDate),
      size: 148,
      minSize: 148,
      enableColumnFilter: true,
      meta: createDateFilterMeta({
        key: 'signReceiptDateFrom',
        label: '預計送交日',
        mode: 'range',
        filterValue: readSignReceiptDateRangeFilter(filters),
        onChange: onSignReceiptDateRangeChange,
        placeholder: '西元年/月/日',
        resolveFilterDisplay: resolveSignReceiptDateFilterChipDisplay,
      }),
    },
    {
      id: 'customerName',
      header: '客戶',
      accessorFn: (row) => displayDash(row.customerName),
      size: 140,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        // 顯示單據客戶名稱快照（排序 customer_name）；篩選走 customer_id 關聯
        ...createAsyncMultiFilterMeta({
          key: 'customerId',
          label: '客戶',
          searchOptions: searchCustomerFilterOptions,
          filterValue: filters.customerId,
          onChange: (value) => onFilterChange('customerId', value),
          resolveFilterDisplay: (values) => (
            resolveFromLabelCache(customerLabelCache, values)
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(customerLabelCache, options, values);
          },
        }),
      },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'contactName',
      header: '聯絡人',
      accessorFn: (row) => displayDash(row.contactName),
      size: 120,
      enableSorting: false,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'contactName',
        label: '聯絡人',
        filterValue: filters.contactName,
        onChange: (value) => onFilterChange('contactName', value),
        placeholder: '搜尋聯絡人',
      }),
    },
    {
      id: 'remark',
      header: '備註',
      accessorFn: (row) => displayDash(row.remark),
      size: 180,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'remark',
          label: '備註',
          filterValue: filters.remark,
          onChange: (value) => onFilterChange('remark', value),
          placeholder: '搜尋備註',
        }),
      },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
  ];
}
